import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { Download } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router';
import { toast } from 'sonner';

import { api, unwrap, unwrapPage } from '@/api/client';
import { mensajeDe } from '@/api/errors';
import type { components, operations } from '@/api/schema';
import { PermissionGate } from '@/auth/PermissionGate';
import { useStepUp } from '@/auth/StepUp';
import { Cargando, Encabezado, ErrorCarga, Vacio } from '@/components/Estados';
import { Paginador } from '@/components/Paginador';
import { useOpcionesLicencia } from '@/features/alumnos/queries';
import { useFiltrosUrl } from '@/hooks/useFiltrosUrl';
import { guardarArchivo } from '@/lib/descargar';
import { fechaHora, mxn } from '@/lib/formato';

type Pago = components['schemas']['PaymentRow'];
type Resumen = components['schemas']['PaymentsSummary'];
type Filtros = NonNullable<operations['getAdminPayments']['parameters']['query']>;

const CLAVES = ['from', 'to', 'provider', 'status', 'membershipId', 'q'] as const;
const PROVEEDORES: Record<string, string> = {
  stripe: 'Stripe',
  paypal: 'PayPal',
  paypal_plus: 'PayPal Plus',
  transfer: 'Transferencia',
  app_store: 'App Store',
  google_play: 'Google Play',
  manual: 'Manual (panel)',
  unknown: 'Otro',
};
const ESTADOS: Record<string, string> = {
  paid: 'Pagado',
  pending: 'Pendiente',
  refunded: 'Reembolsado',
  disputed: 'En disputa',
  failed: 'Fallido',
};

function mesLargo(ym: string): string {
  const [y, m] = ym.split('-').map(Number);
  return new Intl.DateTimeFormat('es-MX', {
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(new Date(Date.UTC(y ?? 2000, (m ?? 1) - 1, 15)));
}

function Pagos() {
  const { filtros, cambiar, query } = useFiltrosUrl(CLAVES);
  const opciones = useOpcionesLicencia();
  const { proteger, dialogo } = useStepUp();
  const [exportando, setExportando] = useState(false);
  const f = query as Filtros;
  const lista = useQuery({
    queryKey: ['pagos', 'lista', f, filtros.page],
    queryFn: () =>
      unwrapPage<Pago[]>(
        api.GET('/api/v1/admin/payments', {
          params: { query: { ...f, page: filtros.page, pageSize: 50 } },
        }),
      ),
    placeholderData: keepPreviousData,
  });
  const resumen = useQuery({
    queryKey: ['pagos', 'resumen', f],
    queryFn: () =>
      unwrap<Resumen>(api.GET('/api/v1/admin/payments/summary', { params: { query: f } })),
    placeholderData: keepPreviousData,
  });
  const exportar = async () => {
    setExportando(true);
    try {
      await proteger(async () =>
        guardarArchivo(
          await api.GET('/api/v1/admin/payments/export', { params: { query: f }, parseAs: 'blob' }),
          'pagos.xlsx',
        ),
      );
    } catch (e) {
      toast.error(mensajeDe(e));
    } finally {
      setExportando(false);
    }
  };
  const r = resumen.data;
  return (
    <>
      <Encabezado
        titulo="Pagos"
        descripcion="Fechas en hora de la Ciudad de México. Los totales solo cuentan pagos pagados."
        acciones={
          <button
            type="button"
            className="btn-cs btn-contorno"
            disabled={exportando}
            onClick={() => void exportar()}
          >
            <Download size={16} aria-hidden /> {exportando ? 'Generando…' : 'Exportar .xlsx'}
          </button>
        }
      />
      <div className="mb-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
        <input
          type="date"
          className="input-vidrio"
          value={filtros.from}
          onChange={(e) => cambiar({ from: e.target.value })}
          aria-label="Desde"
          title="Desde"
        />
        <input
          type="date"
          className="input-vidrio"
          value={filtros.to}
          onChange={(e) => cambiar({ to: e.target.value })}
          aria-label="Hasta"
          title="Hasta"
        />
        <select
          className="input-vidrio"
          value={filtros.provider}
          onChange={(e) => cambiar({ provider: e.target.value })}
          aria-label="Proveedor"
        >
          <option value="">Todos los proveedores</option>
          {Object.entries(PROVEEDORES).map(([k, v]) => (
            <option key={k} value={k}>
              {v}
            </option>
          ))}
        </select>
        <select
          className="input-vidrio"
          value={filtros.status}
          onChange={(e) => cambiar({ status: e.target.value })}
          aria-label="Estado"
        >
          <option value="">Cualquier estado</option>
          {Object.entries(ESTADOS).map(([k, v]) => (
            <option key={k} value={k}>
              {v}
            </option>
          ))}
        </select>
        <select
          className="input-vidrio"
          value={filtros.membershipId}
          onChange={(e) => cambiar({ membershipId: e.target.value })}
          aria-label="Licencia"
        >
          <option value="">Todas las licencias</option>
          {opciones.data?.map((o) => (
            <option key={o.id} value={o.id}>
              {o.name}
            </option>
          ))}
        </select>
        <input
          className="input-vidrio"
          placeholder="Alumno, folio o cupón"
          defaultValue={filtros.q}
          onKeyDown={(e) => e.key === 'Enter' && cambiar({ q: e.currentTarget.value.trim() })}
          onBlur={(e) => cambiar({ q: e.target.value.trim() })}
          aria-label="Buscar"
        />
      </div>
      {r && (
        <div className="mb-6 grid gap-4 lg:grid-cols-[1fr_2fr]">
          <div className="grid grid-cols-2 gap-3">
            {[
              ['Ingresos', mxn(r.total)],
              ['Pagos', r.count.toLocaleString('es-MX')],
              ['Precio regular', mxn(r.regularTotal)],
              ['Descuentos', mxn(r.savings)],
            ].map(([t, v]) => (
              <div key={t} className="card-vidrio p-4">
                <p className="text-xs font-bold text-texto-3 uppercase">{t}</p>
                <p className="mt-1 text-xl font-extrabold tabular">{v}</p>
              </div>
            ))}
          </div>
          <div className="grid gap-4 md:grid-cols-2">
            <div className="panel-vidrio max-h-64 overflow-y-auto p-0">
              <table className="tabla-cs w-full text-sm">
                <thead>
                  <tr>
                    <th>Mes</th>
                    <th>Pagos</th>
                    <th>Ingresos</th>
                  </tr>
                </thead>
                <tbody>
                  {r.byMonth.map((m) => (
                    <tr key={m.month}>
                      <td className="capitalize">{mesLargo(m.month)}</td>
                      <td className="tabular">{m.count}</td>
                      <td className="tabular font-bold">{mxn(m.total)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="panel-vidrio max-h-64 overflow-y-auto p-0">
              <table className="tabla-cs w-full text-sm">
                <thead>
                  <tr>
                    <th>Proveedor</th>
                    <th>Pagos</th>
                    <th>Ingresos</th>
                  </tr>
                </thead>
                <tbody>
                  {r.byProvider.map((p) => (
                    <tr key={p.provider}>
                      <td>{PROVEEDORES[p.provider] ?? p.provider}</td>
                      <td className="tabular">{p.count}</td>
                      <td className="tabular font-bold">{mxn(p.total)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
      {lista.isPending && <Cargando />}
      {lista.isError && (
        <ErrorCarga error={lista.error} onReintentar={() => void lista.refetch()} />
      )}
      {lista.data?.data.length === 0 && <Vacio titulo="Sin pagos con esos filtros" />}
      {lista.data && lista.data.data.length > 0 && (
        <div className="panel-vidrio overflow-x-auto p-0">
          <table className="tabla-cs w-full text-sm">
            <thead>
              <tr>
                <th>Fecha</th>
                <th>Alumno</th>
                <th>Licencia</th>
                <th>Proveedor</th>
                <th>Pagado</th>
                <th>Cupón / beca</th>
                <th>Estado</th>
                <th>Folio</th>
              </tr>
            </thead>
            <tbody>
              {lista.data.data.map((p) => (
                <tr key={p.id}>
                  <td className="whitespace-nowrap">{fechaHora(p.paidAt)}</td>
                  <td>
                    <Link to={`/alumnos/${p.userId}?tab=pagos`} className="font-semibold">
                      {p.userName}
                    </Link>
                    <p className="text-xs text-texto-3">{p.userEmail}</p>
                  </td>
                  <td>{p.membershipName}</td>
                  <td>
                    {PROVEEDORES[p.provider] ?? p.provider}
                    <p className="text-xs text-texto-3">{p.providerLabel}</p>
                  </td>
                  <td className="tabular font-bold whitespace-nowrap">
                    {mxn(p.finalPrice)}
                    {Number(p.savings) > 0 && (
                      <p className="text-xs font-normal text-texto-3">de {mxn(p.regularPrice)}</p>
                    )}
                  </td>
                  <td>{p.couponCode ?? p.scholarshipName ?? '—'}</td>
                  <td>{ESTADOS[p.status] ?? p.status}</td>
                  <td className="font-mono text-xs">{p.folio ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {lista.data && (
        <Paginador
          page={lista.data.page}
          pageSize={lista.data.pageSize}
          total={lista.data.total}
          onPage={(page) => cambiar({ page })}
        />
      )}
      {dialogo}
    </>
  );
}

export function PagosPage() {
  return (
    <PermissionGate seccion="pagos">
      <Pagos />
    </PermissionGate>
  );
}
